import { z } from 'zod';
import { SUPPORT_ANSWER_DOCUMENT_TYPE, SUPPORT_REQUEST_DOCUMENT_TYPE } from '../case/support.js';
/**
 * Índice electrónico del expediente (Acuerdo 001 de 2024 del AGN, que recoge los artículos 9 a 11 del
 * Acuerdo 003 de 2015). Cada documento que entra al expediente agrega una línea con orden consecutivo y
 * huella SHA-256; al cierre el índice se firma (XAdES) y queda congelado. Una reapertura abre una fase
 * nueva (índice complementario) sin tocar la anterior.
 */
export const INDEX_NAMESPACE = 'http://www.archivogeneral.gov.co/normativa/acuerdo001-2024';
export const INDEX_HASH_ALGORITHM = 'SHA-256';
/** OPEN: recibe documentos. SEALING: el expediente cerró y la firma está en curso. SIGNED: congelado. FAILED: la firma falló (se reintenta). */
export const CaseIndexPhaseStatus = {
    OPEN: 'OPEN',
    SEALING: 'SEALING',
    SIGNED: 'SIGNED',
    FAILED: 'FAILED',
};
/** Tipos documentales que se ofrecen por defecto; cada entidad los ajusta en Configuración. */
export const DEFAULT_DOCUMENT_TYPES = ['Petición', 'Anexo', 'Respuesta', 'Correo electrónico', 'Acta', 'Auto', 'Resolución', 'Informe técnico', 'Constancia', 'Otro'];
/** Campo `reason` de POST /documents/:id/replace (multipart, con la versión corregida): por qué se reemplaza. */
export const replaceDocumentSchema = z.object({ reason: z.string().trim().min(3, 'Escribe el motivo (mínimo 3 caracteres)').max(500) });
/** Tipo documental cuando quien sube no elige uno. */
export const DEFAULT_DOCUMENT_TYPE_BY_KIND = {
    ORIGINAL: 'Anexo',
    PETITION: 'Petición',
    EMAIL: 'Correo electrónico',
    COMMUNICATION: 'Comunicación enviada',
    RESPONSE: 'Respuesta',
    EMAIL_RAW: 'Correo electrónico',
    DERIVED: 'Documento derivado',
};
/**
 * Clases de documento. Todo lo que entra al índice es un archivo: lo que llega sin serlo (el texto de la petición,
 * un correo, una comunicación enviada) se guarda como PDF/A. Los adjuntos que no son PDF, y los escaneos, llevan
 * además una copia PDF/A (DERIVED) junto al original.
 */
export const DocumentKind = {
    ORIGINAL: 'ORIGINAL',
    PETITION: 'PETITION',
    EMAIL: 'EMAIL',
    COMMUNICATION: 'COMMUNICATION',
    RESPONSE: 'RESPONSE',
    DERIVED: 'DERIVED',
};
/**
 * Por qué se reabre. `COMPLEMENTARY_RESPONSE`: la respuesta no cubrió todo lo pedido; entra en el paso de responder y
 * sigue el plazo de la petición original (no empieza uno nuevo). `OTHER` (por defecto: recurso, desacato…): plazo nuevo.
 */
export const REOPEN_KINDS = ['COMPLEMENTARY_RESPONSE', 'OTHER'];
export const reopenCaseSchema = z.object({
    reason: z.string().trim().min(10).max(1000),
    kind: z.enum(REOPEN_KINDS).optional(),
    /** Flujo con el que sigue; sin él, el del tipo del caso. */
    workflowCode: z.string().min(1).max(60).optional(),
    /** Paso del flujo en el que se reabre (p. ej. «Cumplir o impugnar» cuando llega el fallo); sin él, desde el inicio. */
    nodeId: z.string().min(1).max(80).optional(),
});
/** Eliminar el expediente al cumplir su retención (disposición final): motivo y el radicado escrito para confirmar. */
export const disposeCaseSchema = z.object({
    reason: z.string().trim().min(10).max(1000),
    confirmation: z.string().trim().min(1).max(80),
});
// ─────────────────────────────────────────── Verificación pública de un documento emitido
/**
 * Código de verificación impreso (con su QR) en los documentos que emite la entidad: petición radicada,
 * comunicaciones y respuesta. Doce símbolos de un alfabeto sin caracteres ambiguos (sin 0, O, 1, I, L):
 * unos 60 bits al azar, imposible de adivinar o recorrer. Se muestra en tres grupos de cuatro.
 */
export const VERIFICATION_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
export const VERIFICATION_CODE_LENGTH = 12;
export const VERIFICATION_CODE_PATTERN = new RegExp(`^[${VERIFICATION_CODE_ALPHABET}]{${VERIFICATION_CODE_LENGTH}}$`);
/** «ABCD-EFGH-JKMN», como va impreso. */
export function formatVerificationCode(code) {
    return code.match(/.{1,4}/g)?.join('-') ?? code;
}
/** Lo que escribe o pega la persona: mayúsculas, sin guiones ni espacios; `null` si no puede ser un código. */
export function normalizeVerificationCode(input) {
    const code = input.toUpperCase().replace(/[^A-Z0-9]/g, '');
    return VERIFICATION_CODE_PATTERN.test(code) ? code : null;
}
/**
 * Documentos que llevan código: los que emite la entidad (nunca lo que adjunta el ciudadano). Un anexo de la respuesta
 * (`ORIGINAL` que subió un funcionario) recibe el suyo cuando sale con un envío certificado: el correo lleva el enlace.
 */
export const VERIFIABLE_DOCUMENT_KINDS = ['PETITION', 'COMMUNICATION', 'RESPONSE'];
/** Tipología de los archivos que quien responde sube como anexos de la respuesta. */
export const RESPONSE_ATTACHMENT_DOCUMENT_TYPE = 'Anexo de la respuesta';
export const RESPONSE_MAX_ATTACHMENTS = 10;
/**
 * Qué documento del expediente puede salir como anexo de la respuesta: uno que subió un funcionario. Nunca lo del
 * ciudadano ni lo que genera la plataforma, ni los apoyos de otras áreas (trabajo interno).
 */
export function isResponseAttachable(d) {
    return d.kind === 'ORIGINAL' && d.uploadedByKind === 'USER' && !d.deletedAt && !d.replacedAt && d.documentType !== SUPPORT_REQUEST_DOCUMENT_TYPE && d.documentType !== SUPPORT_ANSWER_DOCUMENT_TYPE;
}
// ─────────────────────────────────────────── Firma del índice: certificado y TSA de la entidad
/** Cargar el certificado de firma (.p12/.pfx) desde Configuración. El archivo va en base64. */
export const uploadSigningCertificateSchema = z.object({
    fileName: z.string().trim().min(1).max(200),
    /** Unos pocos KB en la práctica; el límite evita cuerpos enormes. */
    p12Base64: z.string().min(16).max(1_400_000).regex(/^[A-Za-z0-9+/=\s]+$/, 'El archivo no está en base64'),
    password: z.string().max(500),
});
/** Servicio de estampa de tiempo. `password`: sin el campo se conserva la guardada; `null` la quita. */
export const timestampAuthoritySchema = z.object({
    url: z.string().trim().url().max(500).refine((u) => /^https?:\/\//i.test(u), 'La URL debe empezar por http:// o https://'),
    user: z.string().trim().max(200).optional().nullable(),
    password: z.string().max(500).optional().nullable(),
});
/** Probar una TSA: con los datos del formulario o, si faltan, con los guardados. */
export const testTimestampAuthoritySchema = timestampAuthoritySchema.partial();
//# sourceMappingURL=archive.js.map